import { useEffect, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";

const LANGUAGES = ["IT", "DE", "FR", "EN"];
const text = (value) => typeof value === "string" ? value.trim() :
  typeof value === "number" && Number.isFinite(value) ? String(value) : "";
const localized = (item, field) => LANGUAGES.map((lang) => text(item[field + lang])).find(Boolean) || "";
const searchable = (item) => ["articleNumber", "barcode",
  ...LANGUAGES.flatMap((lang) => ["name" + lang, "description" + lang])]
  .map((key) => text(item[key])).join(" ").toLocaleLowerCase();

const LABELS = {
  LOADING: "Lettura in corso…",
  ERROR: "Errore di lettura KLARA. Riprova con Aggiorna.",
  NOT_CONFIGURED: "Integrazione non configurata per questa azienda.",
  EMPTY: "Nessun articolo restituito da KLARA",
  DATA: "Lettura riuscita",
  SCHEMA_ERROR: "Risposta KLARA non compatibile. Impossibile mostrare gli articoli.",
};

function validEnvelope(data, companyId) {
  return data && data.source === "KLARA" && data.company_id === companyId &&
    data.completeness === "UNKNOWN" && typeof data.read_at === "string" &&
    Number.isFinite(Date.parse(data.read_at)) && Array.isArray(data.items) &&
    data.items.every((item) => item && typeof item === "object" && !Array.isArray(item));
}

export default function KlaraIntegration() {
  const { companyId, company } = useCompany();
  // Remount on company change: no previous-company state survives even one render.
  return <CompanyArticles key={companyId || "none"} companyId={companyId} companyName={company?.name} />;
}

function CompanyArticles({ companyId, companyName }) {
  const [result, setResult] = useState({ status: "LOADING", items: null });
  const [lastRead, setLastRead] = useState(null);
  const [query, setQuery] = useState("");
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!companyId) return;
    let active = true;
    const controller = new AbortController();
    setResult({ status: "LOADING", items: null });
    api.get("/integrations/klara/articles", {
      params: { limit: 25 }, signal: controller.signal,
      headers: { "x-company-id": companyId },
    }).then(({ data }) => {
      if (!active) return;
      if (!validEnvelope(data, companyId)) {
        setResult({ status: "SCHEMA_ERROR", items: null });
        return;
      }
      setLastRead(data.read_at);
      setResult({ status: data.items.length ? "DATA" : "EMPTY", items: data.items });
    }).catch((error) => {
      if (!active) return;
      const code = error.response?.data?.detail?.code;
      const status = code === "KLARA_NOT_CONFIGURED" ? "NOT_CONFIGURED" :
        code === "KLARA_SCHEMA_ERROR" ? "SCHEMA_ERROR" : "ERROR";
      setResult({ status, items: null });
    });
    return () => { active = false; controller.abort(); };
  }, [companyId, revision]);

  const filtered = (result.items || []).filter((item) => searchable(item).includes(query.trim().toLocaleLowerCase()));
  const failed = ["ERROR", "NOT_CONFIGURED", "SCHEMA_ERROR"].includes(result.status);

  return (
    <section className="max-w-6xl mx-auto px-4 space-y-5" aria-labelledby="klara-title">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs tracking-widest text-cyan-300">INTEGRAZIONI</p>
          <h1 id="klara-title" className="font-display text-3xl font-bold mt-1">KLARA</h1>
          <p className="text-gray-400 mt-1">Integrazione read-only</p>
          <p className="text-sm text-gray-300 mt-3 break-words">Azienda DAD: {companyName || "Nessuna azienda selezionata"}</p>
        </div>
        <button type="button" className="btn-primary min-h-[44px]" disabled={!companyId || result.status === "LOADING"}
          onClick={() => { setResult({ status: "LOADING", items: null }); setRevision((value) => value + 1); }}>
          <RefreshCw size={16} aria-hidden="true" /> Aggiorna
        </button>
      </header>

      <div className="glass rounded-2xl border border-cyan-400/20 p-5 space-y-4">
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div><dt className="text-gray-400">Fonte</dt><dd className="text-cyan-300 mt-1">KLARA</dd></div>
          <div><dt className="text-gray-400">Ultima lettura riuscita</dt><dd className="mt-1">{lastRead ?
            <time dateTime={lastRead}>{new Date(lastRead).toLocaleString("it-CH")}</time> : "Non disponibile"}</dd></div>
          <div><dt className="text-gray-400">Articoli caricati</dt><dd className="mt-1" data-testid="klara-count">{result.items === null ? "—" : result.items.length}</dd></div>
        </dl>
        <p className="text-sm text-gray-400">Completezza: <span className="text-amber-300">UNKNOWN</span> — risultati potenzialmente parziali. La lettura non certifica l’intero catalogo.</p>
        <p role={failed ? "alert" : "status"} data-testid="klara-state" data-state={result.status}
          className={failed ? "text-amber-300 text-sm" : "text-cyan-200 text-sm"}>
          {companyId ? LABELS[result.status] : "Seleziona un’azienda DAD per leggere gli articoli."}
        </p>
      </div>

      {result.status === "DATA" && <>
        <div>
          <label htmlFor="klara-search" className="block text-sm text-gray-300 mb-2">Cerca nei risultati caricati</label>
          <div className="relative">
            <Search size={18} aria-hidden="true" className="absolute left-3 top-3.5 text-gray-500" />
            <input id="klara-search" type="search" className="input-dark !pl-10 min-h-[44px]" value={query}
              onChange={(event) => setQuery(event.target.value)} />
          </div>
          <p className="text-xs text-gray-400 mt-2" role="status">{filtered.length} risultati visibili su {result.items.length} caricati. Ricerca locale.</p>
        </div>
        {filtered.length === 0 ? <p className="text-gray-400">Nessuna corrispondenza nei risultati caricati.</p> :
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" aria-label="Articoli KLARA">
            {filtered.map((item, index) => <li key={index} className="glass rounded-2xl border border-cyan-400/10 p-5 min-w-0 break-words">
              <h2 className="font-display font-semibold text-lg text-gray-100">{localized(item, "name") || "Nome non disponibile"}</h2>
              {localized(item, "description") && <p className="text-sm text-gray-400 mt-2 whitespace-pre-wrap">{localized(item, "description")}</p>}
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {[["Numero articolo", text(item.articleNumber)], ["Unità", localized(item, "unit")],
                  ["Codice a barre", text(item.barcode)], ["Tipo prodotto", text(item.productType)]]
                  .filter(([, value]) => value).map(([label, value]) => <div key={label}>
                    <dt className="text-gray-500">{label}</dt><dd className="text-gray-200 mt-1">{value}</dd>
                  </div>)}
              </dl>
            </li>)}
          </ul>}
      </>}
    </section>
  );
}
